import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { Action, State, StateContext, Store } from "@ngxs/store";
import { mapTo } from "rxjs/operators";
import { uniq } from "lodash";
import { dispatchRequest } from "../dispatch-request";

import {
  GetVehicleInfoAction,
  GetVehicleInfoFailAction,
  GetVehicleInfoSuccessAction,
  ResetCurrentVehicleIdAction,
  SaveVehicleInfoAction,
  SaveVehicleInfoFailAction,
  SaveVehicleInfoSuccessAction,
} from "./vehicle-info.actions";
import { VehicleInfo } from "../../core/models/vehicle-info.model";
import { UpdateActiveVehicleAfterUpdatingItsInfoAction } from "../active-vehicle/active-vehicle.actions";
import { CostCentreGetterState } from "../cost-centre";
import { UpdateVehicleAfterUpdatingItsInfoAction } from "../vehicle/vehicle.actions";
import { ToastService } from "../../core/services";
import {
  defaultRequestState,
  IRequestsNestedState,
} from "../interfaces/requests.interface";

export interface VehicleInfoStateModel {
  entities: { [key: number]: VehicleInfo };
  ids: number[];
  currentVehicleId: number;
  saveVehicleInfoPutRequestState: IRequestsNestedState;
}

@State<VehicleInfoStateModel>({
  name: "vehicleInfo",
  defaults: {
    entities: {},
    ids: [],
    currentVehicleId: null,
    saveVehicleInfoPutRequestState: { ...defaultRequestState },
  },
})
@Injectable()
export class VehicleInfoState {
  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private toaster: ToastService
  ) {}

  @Action(GetVehicleInfoAction)
  getVehicleInfo(
    ctx: StateContext<VehicleInfoStateModel>,
    action: GetVehicleInfoAction
  ) {
    const request = this.httpClient.get(`GetVehicle`, {
      params: {
        vehicleId: `${action.payload}`,
      },
    });

    return dispatchRequest(
      request,
      ctx,
      GetVehicleInfoSuccessAction,
      GetVehicleInfoFailAction
    );
  }

  @Action(GetVehicleInfoSuccessAction)
  getVehicleInfoSuccess(
    ctx: StateContext<VehicleInfoStateModel>,
    action: GetVehicleInfoSuccessAction
  ) {
    const currentVehicle = action.payload;
    ctx.patchState({
      entities: {
        [currentVehicle.id]: currentVehicle,
      },
      ids: uniq([...ctx.getState().ids, currentVehicle.id]),
      currentVehicleId: currentVehicle.id,
    });
  }

  @Action(SaveVehicleInfoAction)
  saveVehicleInfo(
    ctx: StateContext<VehicleInfoStateModel>,
    action: SaveVehicleInfoAction
  ) {
    const vehicleInfo = action.payload;
    const request = this.httpClient
      .put(`SaveVehicleInfo`, vehicleInfo)
      .pipe(mapTo(vehicleInfo));

    return dispatchRequest(
      request,
      ctx,
      SaveVehicleInfoSuccessAction,
      SaveVehicleInfoFailAction,
      "saveVehicleInfoPutRequestState"
    );
  }

  @Action(SaveVehicleInfoSuccessAction)
  saveVehicleInfoSuccess(
    ctx: StateContext<VehicleInfoStateModel>,
    action: SaveVehicleInfoSuccessAction
  ) {
    const vehicleInfo = action.payload;

    const costCentres = this.store.selectSnapshot(
      CostCentreGetterState.getCostCentres
    );
    const costCentre = costCentres.find(
      (centre) => centre.costCentreId === vehicleInfo.costCentreId
    );

    const updatedDataForActiveVehicle = {
      id: vehicleInfo.id,
      regoNumber: vehicleInfo.registrationNumber,
      driver: vehicleInfo.driver,
      clientReference: vehicleInfo.clientReference,
      icon: vehicleInfo.icon,
      costCentre: vehicleInfo.costCentreId,
      costCentreName: costCentre?.costCentreName,
      startOdo: vehicleInfo.startOdometer,
    };

    const updatedDataForVehicle = {
      id: vehicleInfo.id,
      vehRegoNo: vehicleInfo.registrationNumber,
      vehDriver: vehicleInfo.driver,
      vehCostCentreId: vehicleInfo.costCentreId,
    };

    this.toaster.showToast("Saved successfully");
    ctx.dispatch([
      new UpdateActiveVehicleAfterUpdatingItsInfoAction(
        updatedDataForActiveVehicle
      ),
      new UpdateVehicleAfterUpdatingItsInfoAction(updatedDataForVehicle),
    ]);
  }

  @Action(ResetCurrentVehicleIdAction)
  resetCurrentVehicleId(ctx: StateContext<VehicleInfoStateModel>) {
    ctx.patchState({
      currentVehicleId: null,
    });
  }
}
